import UnionBar from "@/assets/img/today/moon/union_bar.webp"
import PipkaLeft from "@/assets/img/today/moon/tooltip/pipka_left_2x.webp"
import PipkaCenter from "@/assets/img/today/moon/tooltip/pipka_enter_2x.webp"
import PipkaRight from "@/assets/img/today/moon/tooltip/pipka_right_2x.webp"

import { type DistanceData, formatMoonDate } from "../../config/moon-content"
import type { MoonPhasesPayload } from "../../types"

const NUMBER = new Intl.NumberFormat("en-US")
const formatKm = (km: number) => `${NUMBER.format(km)} km`
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))

const positionRatio = (data: DistanceData): number => {
  const { perigee, apogee, nextPerigee } = data.points
  const ascending = apogee.distanceKm - perigee.distanceKm
  const descending = apogee.distanceKm - nextPerigee.distanceKm
  if (ascending <= 0 || descending <= 0) return 0
  if (data.current.phase === "toPerigee") {
    return 0.5 + clamp((apogee.distanceKm - data.current.distanceKm) / descending, 0, 1) * 0.5
  }
  return clamp((data.current.distanceKm - perigee.distanceKm) / ascending, 0, 1) * 0.5
}

interface DistanceFromEarthProps {
  distance?: MoonPhasesPayload["distance"]
}

/** "Distance from Earth" perigee/apogee track. Ported from MoonPhase DistanceFromEarth. */
export function DistanceFromEarth({ distance }: DistanceFromEarthProps) {
  if (!distance) return null

  const data: DistanceData = {
    current: {
      distanceKm: distance.current.distance_km,
      phase: distance.current.phase,
    },
    points: distance.points,
  }
  const ratio = positionRatio(data)
  const markerLeft = `${ratio * 100}%`
  const variant = ratio <= 0.15 ? "left" : ratio >= 0.85 ? "right" : "center"
  const pipka = variant === "left" ? PipkaLeft : variant === "right" ? PipkaRight : PipkaCenter
  const tooltipTransform =
    variant === "left"
      ? "translateX(0)"
      : variant === "right"
        ? "translateX(-100%)"
        : "translateX(-50%)"

  const points = [
    { label: "Perigee", align: "items-start", point: data.points.perigee },
    { label: "Apogee", align: "items-center", point: data.points.apogee },
    { label: "Next Perigee", align: "items-end", point: data.points.nextPerigee },
  ]

  return (
    <div className="mt-6 px-4">
      <div className="flex flex-col gap-4 rounded-3xl bg-gradient-to-bl from-purple-light/10 to-purple-light/5 p-4 backdrop-blur-2xl">
        <h3 className="font-display text-xl font-medium text-purple-light">Distance from Earth</h3>

        <div className="relative h-[140px] w-full">
          {/* Tooltip */}
          <div
            className="absolute top-0 flex h-[56.5px] w-[97px] flex-col items-center justify-center bg-[length:100%_100%] bg-center bg-no-repeat px-2 pb-2.5 pt-1.5 text-center"
            style={{
              left: markerLeft,
              transform: tooltipTransform,
              backgroundImage: `url(${pipka})`,
            }}
          >
            <span className="font-body text-sm text-purple-light">Today</span>
            <span className="whitespace-nowrap font-body text-sm font-medium text-purple-light">
              {formatKm(data.current.distanceKm)}
            </span>
          </div>

          {/* Track */}
          <div className="absolute inset-x-0 top-[65px] h-3">
            <div
              className="absolute top-1/2 left-0 h-0.5 -translate-y-1/2 overflow-hidden"
              style={{ width: markerLeft }}
            >
              <div className="h-0.5 w-[390px] bg-gradient-to-r from-[#FF537F] via-[#FF9B00] to-[#7FE718]" />
            </div>
            <div className="flex min-h-3 items-center justify-center">
              <img src={UnionBar} alt="" />
            </div>
            <span className="absolute top-1/2 left-0 size-3 -translate-y-1/2 rounded-full bg-peach" />
            {data.current.phase === "toPerigee" && (
              <span className="absolute top-1/2 left-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FF9B00]" />
            )}
            <span
              className="absolute top-1/2 h-3 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-purple-light"
              style={{ left: markerLeft }}
            />
          </div>

          {/* Point labels */}
          <div className="absolute inset-x-0 top-[81px] grid grid-cols-3 gap-2">
            {points.map(({ label, align, point }) => (
              <div key={label} className={`flex flex-col gap-0.5 ${align}`}>
                <span className="whitespace-nowrap font-body text-sm font-medium text-purple-light">
                  {label}
                </span>
                <span className="whitespace-nowrap font-body text-sm text-purple-light/70">
                  {formatMoonDate(point.dateTime)}
                </span>
                <span className="whitespace-nowrap font-body text-sm text-purple-light/70">
                  {formatKm(point.distanceKm)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
